import { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calculator } from "lucide-react";
import { Link } from "react-router-dom";

const TERMS = [12, 24, 36, 48, 60, 72, 84];

function monthlyPayment(principal, annualAprPct, months) {
  const r = annualAprPct / 100 / 12;
  if (r === 0) return principal / months;
  return (principal * r) / (1 - Math.pow(1 + r, -months));
}

export default function LoanCalculator() {
  const [amount, setAmount] = useState(15000);
  const [apr, setApr] = useState(9.99); // illustrative placeholder
  const [term, setTerm] = useState(60);

  const pay = useMemo(() => monthlyPayment(amount, apr, term), [amount, apr, term]);
  const totalPaid = pay * term;
  const totalInterest = Math.max(0, totalPaid - amount);

  return (
    <div className="ef-card p-7 md:p-9" data-testid="loan-calculator">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-lg bg-[#1B63F0]/15 border border-[#1B63F0]/40 flex items-center justify-center">
          <Calculator className="h-4 w-4 text-[#1B63F0]" />
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#D4AF37]">Loan Calculator</div>
          <div className="font-display text-xl font-bold">Estimate your monthly payment</div>
        </div>
      </div>

      <div className="mt-7 grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-6">
          {/* Amount */}
          <div>
            <div className="flex items-baseline justify-between">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">Loan Amount</div>
              <div className="font-display text-2xl font-bold ef-gold-text" data-testid="calc-amount-val">${amount.toLocaleString()}</div>
            </div>
            <Slider
              value={[amount]}
              min={1000}
              max={100000}
              step={500}
              onValueChange={(v) => setAmount(v[0])}
              className="mt-3"
              data-testid="calc-amount-slider"
            />
            <div className="mt-1.5 flex justify-between text-[10px] font-mono text-white/40">
              <span>$1,000</span><span>$100,000</span>
            </div>
          </div>

          {/* APR */}
          <div>
            <div className="flex items-baseline justify-between">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">Estimated APR</div>
              <div className="font-display text-2xl font-bold" data-testid="calc-apr-val">{apr.toFixed(2)}%</div>
            </div>
            <Slider
              value={[apr]}
              min={5.99}
              max={35.99}
              step={0.25}
              onValueChange={(v) => setApr(v[0])}
              className="mt-3"
              data-testid="calc-apr-slider"
            />
            <div className="mt-1.5 flex justify-between text-[10px] font-mono text-white/40">
              <span>5.99%</span><span>35.99%</span>
            </div>
          </div>

          {/* Term */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">Loan Term (months)</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {TERMS.map((t) => (
                <button
                  key={t}
                  onClick={() => setTerm(t)}
                  data-testid={`calc-term-${t}`}
                  className={`px-3.5 h-9 rounded-full text-xs font-mono border transition-all ${
                    term === t
                      ? "bg-[#1B63F0] border-[#1B63F0] text-white"
                      : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
                  }`}
                >
                  {t} mo
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="lg:col-span-2 flex flex-col justify-between">
          <div className="rounded-2xl bg-gradient-to-br from-[#17A673]/15 to-[#1B63F0]/10 border border-white/10 p-6">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#17A673]">Estimated Monthly Payment</div>
            <div className="mt-2 font-display text-4xl font-bold" data-testid="calc-monthly">
              ${pay.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              <span className="text-base font-normal text-white/50"> /mo</span>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-4 text-xs">
              <div>
                <div className="text-white/50 font-mono uppercase tracking-widest text-[10px]">Total Interest</div>
                <div className="mt-1 font-semibold text-white" data-testid="calc-total-interest">
                  ${totalInterest.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </div>
              </div>
              <div>
                <div className="text-white/50 font-mono uppercase tracking-widest text-[10px]">Total Paid</div>
                <div className="mt-1 font-semibold text-white" data-testid="calc-total-paid">
                  ${totalPaid.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </div>
              </div>
            </div>
          </div>

          <Link to={`/apply?amount=${amount}&term=${term}`}>
            <Button data-testid="calc-apply-btn" className="ef-btn-primary rounded-full w-full h-12 mt-5">
              Apply for {`$${amount.toLocaleString()}`} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
          <p className="mt-3 text-[10px] leading-relaxed text-white/40">
            Illustrative only. Actual APR, term, fees, and payment are determined by the lending partner based on credit and other factors. Not a credit offer or commitment to lend.
          </p>
        </div>
      </div>
    </div>
  );
}
